import React, { useState } from "react";
import Navbar from "../components/Navbar";
import Contact from "../components/Contact";
import Breadcrumb from "../components/Breadcrumb";
import MarineSparesAdvantages from "../components/MarineSparesAdvantages";
import useSEO from "../hooks/useSEO";
import { SITE } from "../constants/siteConfig";
import { COLORS, TYPOGRAPHY, SPACING, RADIUS, SHADOWS, TRANSITIONS, GLASSMORPHISM, HERO_HEIGHTS } from "../constants/designTokens";

const subItems = [
  "Pressure & Temperature Transmitters",
  "RTDs, Thermocouples & Thermowells",
  "Marine Gauges & Digital Calibrators",
  "Level Switches & Ultrasonic Flow Meters",
  "Pneumatic Positioners & I/P Converters",
  "PID Controllers & Signal Isolators"
];

const faqs = [
  {
    question: "Are your pressure and temperature transmitters calibrated and certified?",
    answer:
      "Yes. Every transmitter we supply is factory calibrated and shipped with a full calibration certificate traceable to recognized standards. Where required for classification society surveys, we can also provide certificates of conformity and test reports alongside the equipment."
  },
  {
    question: "How do I know whether I need an RTD or a thermocouple for my engine room application?",
    answer:
      "It depends on the temperature range, accuracy requirement, and response time of your application. RTDs generally offer higher accuracy and stability at moderate engine room temperatures, while thermocouples handle wider and higher temperature ranges. Our technical team can review your existing equipment specification or send you compatible options once you share the make, model, or part number."
  },
  {
    question: "Will an ultrasonic flow meter or level switch be compatible with my existing control system?",
    answer:
      "We check output signal type, process connection, and communication protocol (4-20mA, HART, Modbus, etc.) against your existing installation before confirming a match. If an exact replacement is unavailable, we source a compatible equivalent and confirm compatibility with your system before shipment."
  },
  {
    question: "Can you supply a direct replacement for a PID controller that has failed or been discontinued?",
    answer:
      "In most cases, yes. We maintain sourcing relationships with Danfoss, WIKA, Emerson, and Yokogawa, along with a broader network for discontinued or obsolete controller models. Send us the existing unit's model and wiring details and we will identify a direct replacement or a suitable equivalent with matching functionality."
  },
  {
    question: "Do you provide calibration documentation for classification society and audit purposes?",
    answer:
      "Yes. All instrumentation spares are supplied with calibration certificates, manufacturer documentation, and traceability records needed for classification society surveys, PSC inspections, and internal quality audits. Let us know the documentation format required by your class society and we will match it."
  }
];

const Instrumentation = () => {
  useSEO({
    title: "Marine Instrumentation & Control",
    description:
      "Precision measurement sensors and automation controls for engine room operations. Pressure & temperature transmitters, RTDs, gauges, flow meters, and PID controllers from Danfoss, WIKA, Emerson, and Yokogawa.",
    canonical: "/instrumentation",
    ogImage: "https://marine-sealink.com/img/instrumentation.jpg",
    breadcrumb: [
      { name: "Home", url: "/" },
      { name: "Instrumentation & Control", url: "/instrumentation" }
    ],
    faqs
  });

  const [openIndex, setOpenIndex] = useState(null);

  const toggleFAQ = idx => {
    setOpenIndex(openIndex === idx ? null : idx);
  };

  const whatsappMessage = encodeURIComponent(
    "Hello, I need assistance with marine instrumentation & control spare parts."
  );

  return (
    <div className="page-transition">
      <Breadcrumb items={[{ label: "Home", href: "/" }, { label: "Instrumentation & Control", href: "/instrumentation" }]} />
      <style>{`
        .instrumentation-hero {
          background: linear-gradient(135deg, rgba(8,32,50,0.85), rgba(64,168,196,0.75)), url('/img/instrumentation.jpg') center center/cover no-repeat;
        }

        @media (max-width: 767.98px) {
          .instrumentation-hero > div {
            max-width: 90% !important;
            padding: 2rem 1.5rem !important;
          }
          .instrumentation-hero h1 {
            font-size: 1.75rem !important;
          }
          .instrumentation-hero p {
            font-size: 1rem !important;
          }
          .detail-card {
            padding: 1.5rem !important;
          }
          .cta-section h2 {
            font-size: 1.5rem !important;
          }
          .cta-buttons {
            flex-direction: column !important;
            gap: 1rem !important;
          }
          .cta-btn {
            width: 100% !important;
            justify-content: center !important;
          }
        }

        @media (max-width: 480px) {
          .instrumentation-hero > div {
            padding: 1.5rem 1rem !important;
          }
        }

        .detail-card {
          background: ${COLORS.background.white};
          border-radius: ${RADIUS.xl};
          padding: ${SPACING["2xl"]};
          box-shadow: ${SHADOWS.md};
          border: 1px solid ${COLORS.ui.border};
        }

        .detail-items {
          list-style: none;
          padding: 0;
          margin: ${SPACING.lg} 0;
        }

        .detail-items li {
          padding: ${SPACING.xs} 0;
          color: ${COLORS.text.primary};
          font-size: ${TYPOGRAPHY.fontSize.base};
          display: flex;
          align-items: center;
          gap: ${SPACING.sm};
        }

        .detail-items li::before {
          content: '';
          width: 8px;
          height: 8px;
          background: ${COLORS.primary.teal};
          border-radius: 50%;
          flex-shrink: 0;
        }

        .brands-tag {
          background: ${COLORS.background.lightTeal};
          border-radius: ${RADIUS.lg};
          padding: ${SPACING.sm} ${SPACING.md};
          font-size: ${TYPOGRAPHY.fontSize.sm};
          color: ${COLORS.primary.dark};
          display: inline-block;
          margin-top: ${SPACING.md};
        }

        .brands-tag strong {
          color: ${COLORS.primary.teal};
        }

        .cta-section {
          background: linear-gradient(135deg, ${COLORS.primary.dark} 0%, #1a3a4f 50%, ${COLORS.primary.teal} 100%);
          position: relative;
          overflow: hidden;
        }

        .cta-section::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.03'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
          opacity: 0.5;
        }

        .cta-btn {
          display: inline-flex;
          align-items: center;
          gap: ${SPACING.sm};
          padding: ${SPACING.md} ${SPACING.xl};
          border-radius: ${RADIUS.lg};
          font-weight: ${TYPOGRAPHY.fontWeight.semibold};
          font-size: ${TYPOGRAPHY.fontSize.base};
          text-decoration: none;
          transition: ${TRANSITIONS.all};
          border: 2px solid transparent;
        }

        .cta-btn-primary {
          background: ${COLORS.background.white};
          color: ${COLORS.primary.dark};
          box-shadow: 0 4px 20px rgba(255, 255, 255, 0.2);
        }

        .cta-btn-primary:hover {
          background: ${COLORS.primary.teal};
          color: ${COLORS.text.white};
          transform: translateY(-3px);
          box-shadow: 0 8px 30px rgba(64, 168, 196, 0.4);
        }

        .cta-btn-whatsapp {
          background: ${COLORS.ui.whatsapp};
          color: ${COLORS.text.white};
          box-shadow: 0 4px 20px rgba(37, 211, 102, 0.3);
        }

        .cta-btn-whatsapp:hover {
          background: ${COLORS.ui.whatsappHover};
          color: ${COLORS.text.white};
          transform: translateY(-3px);
          box-shadow: 0 8px 30px rgba(37, 211, 102, 0.5);
        }

        .faq-section h2 {
          color: ${COLORS.primary.dark};
          font-weight: ${TYPOGRAPHY.fontWeight.bold};
          text-align: center;
          margin-bottom: ${SPACING.md};
        }
        .faq-section .section-subtitle {
          color: ${COLORS.text.primary};
          font-size: ${TYPOGRAPHY.fontSize.base};
          text-align: center;
          max-width: 600px;
          margin: 0 auto ${SPACING["2xl"]};
        }
        .faq-card {
          border-radius: ${RADIUS.lg};
          box-shadow: ${SHADOWS.sm};
          margin-bottom: ${SPACING.md};
          overflow: hidden;
          border: 1px solid ${COLORS.ui.border};
          transition: all 0.3s ease;
        }
        .faq-card:hover {
          box-shadow: ${SHADOWS.md};
        }
        .faq-header {
          background: ${COLORS.background.lightBlue};
          cursor: pointer;
          padding: ${SPACING.lg} ${SPACING.xl};
          transition: all 0.3s ease;
        }
        .faq-header:hover {
          background: ${COLORS.background.lightTeal};
        }
        .faq-header h5 {
          color: ${COLORS.primary.teal};
          font-weight: ${TYPOGRAPHY.fontWeight.semibold};
          font-size: ${TYPOGRAPHY.fontSize.md};
          margin: 0;
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: ${SPACING.md};
        }
        .faq-header h5 span {
          flex-shrink: 0;
          font-weight: ${TYPOGRAPHY.fontWeight.normal};
          font-size: 1.5rem;
          line-height: 1;
          transition: transform 0.3s ease;
        }
        .faq-card.open .faq-header h5 span {
          transform: rotate(45deg);
        }
        .faq-body {
          color: ${COLORS.text.primary};
          font-size: ${TYPOGRAPHY.fontSize.base};
          background: ${COLORS.background.lightTeal};
          padding: ${SPACING.lg} ${SPACING.xl};
          line-height: ${TYPOGRAPHY.lineHeight.relaxed};
          border-top: 1px solid rgba(64, 168, 196, 0.2);
        }

        @media (max-width: 767.98px) {
          .faq-header {
            min-height: 60px;
            display: flex;
            align-items: center;
            padding: ${SPACING.md} ${SPACING.lg};
          }
          .faq-header h5 {
            font-size: 1rem !important;
            line-height: 1.4;
          }
          .faq-body {
            font-size: 0.95rem !important;
            padding: ${SPACING.md} ${SPACING.lg};
          }
        }

        @media (max-width: 480px) {
          .faq-header h5 {
            font-size: 0.95rem !important;
          }
          .faq-body {
            font-size: 0.9rem !important;
          }
        }
      `}</style>

      <Navbar />

      {/* Hero Section with image background + navy/teal overlay */}
      <section
        className="instrumentation-hero d-flex align-items-center justify-content-center"
        style={{
          minHeight: HERO_HEIGHTS.page,
          position: "relative",
          color: COLORS.text.white,
          textAlign: "center"
        }}
      >
        <div
          style={{
            background: GLASSMORPHISM.strong.background,
            backdropFilter: GLASSMORPHISM.strong.backdropFilter,
            WebkitBackdropFilter: GLASSMORPHISM.strong.WebkitBackdropFilter,
            padding: `${SPACING["2xl"]} ${SPACING.xl}`,
            borderRadius: RADIUS["2xl"],
            boxShadow: GLASSMORPHISM.strong.boxShadow,
            border: GLASSMORPHISM.strong.border,
            maxWidth: "700px",
            margin: "0 auto"
          }}
        >
          <h1 style={{
            fontWeight: TYPOGRAPHY.fontWeight.bold,
            fontSize: TYPOGRAPHY.fontSize["3xl"],
            marginBottom: SPACING.lg,
            lineHeight: TYPOGRAPHY.lineHeight.tight
          }}>Marine Instrumentation & Control</h1>
          <p style={{
            fontSize: TYPOGRAPHY.fontSize.xl,
            lineHeight: TYPOGRAPHY.lineHeight.relaxed,
            marginBottom: 0
          }}>
            Precision measurement sensors and automation controls essential for engine room operations, sourced and delivered with full calibration and compliance documentation.
          </p>
        </div>
      </section>

      {/* Category Detail Section */}
      <section className="py-5" style={{ background: COLORS.background.light }}>
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-9">
              <div className="detail-card">
                <h2 style={{
                  color: COLORS.primary.dark,
                  fontWeight: TYPOGRAPHY.fontWeight.bold,
                  fontSize: TYPOGRAPHY.fontSize["2xl"],
                  marginBottom: SPACING.md
                }}>
                  Instrumentation & Control
                </h2>
                <p style={{
                  color: COLORS.text.primary,
                  fontSize: TYPOGRAPHY.fontSize.lg,
                  lineHeight: TYPOGRAPHY.lineHeight.relaxed
                }}>
                  Precision measurement sensors and automation controls essential for engine room operations.
                </p>
                <ul className="detail-items">
                  {subItems.map((item, idx) => (
                    <li key={idx}>{item}</li>
                  ))}
                </ul>
                <div className="brands-tag">
                  <strong>Supported Brands:</strong> Danfoss, WIKA, Emerson, Yokogawa
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section py-5">
        <div className="container position-relative" style={{ zIndex: 1 }}>
          <div className="text-center" style={{ color: COLORS.text.white }}>
            <h2 style={{
              fontWeight: TYPOGRAPHY.fontWeight.bold,
              fontSize: TYPOGRAPHY.fontSize["2xl"],
              marginBottom: SPACING.md
            }}>
              Need Instrumentation Spares? Get a Quote Today
            </h2>
            <p style={{
              fontSize: TYPOGRAPHY.fontSize.lg,
              marginBottom: SPACING.xl,
              maxWidth: "600px",
              margin: "0 auto",
              opacity: 0.9,
              lineHeight: TYPOGRAPHY.lineHeight.relaxed,
              paddingBottom: SPACING.xl
            }}>
              Whether you need urgent transmitters, gauges, or control components, our team is ready to source and deliver certified instrumentation parts for your vessel.
            </p>
            <div className="cta-buttons d-flex justify-content-center gap-3 flex-wrap">
              <a href="#contact" className="cta-btn cta-btn-primary">
                <i className="fa-solid fa-file-invoice"></i>
                Request a Quote
              </a>
              <a
                href={`${SITE.whatsapp}?text=${whatsappMessage}`}
                target="_blank"
                rel="noopener noreferrer"
                className="cta-btn cta-btn-whatsapp"
              >
                <i className="fa-brands fa-whatsapp"></i>
                WhatsApp Us
              </a>
            </div>
          </div>
        </div>
      </section>

      <div className="fade-in-section">
        <MarineSparesAdvantages />
      </div>

      {/* FAQ Section */}
      <section className="faq-section py-5" style={{ background: COLORS.background.white }}>
        <div className="container">
          <h2>Frequently Asked Questions</h2>
          <p className="section-subtitle">
            Find answers to common questions about our marine instrumentation & control spare parts.
          </p>
          <div className="accordion" id="instrumentationFAQ">
            {faqs.map((faq, idx) => (
              <div
                className={`faq-card ${openIndex === idx ? "open" : ""}`}
                key={idx}
              >
                <div
                  className="faq-header"
                  onClick={() => toggleFAQ(idx)}
                >
                  <h5>
                    {faq.question}
                    <span>{openIndex === idx ? "−" : "+"}</span>
                  </h5>
                </div>
                {openIndex === idx && (
                  <div className="faq-body">
                    {faq.answer}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="fade-in-section">
        <Contact />
      </div>
    </div>
  );
};

export default Instrumentation;
